import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import DineroFactory from "dinero.js"
import dayjs from "dayjs"

import { Currency } from "helpers/money"
import {
  Order,
  OrderAddress,
  OrderGiftCardProductRetain24,
  OrderState
} from "lib/types/generated/graphql-types"

const PAGE_LEFT = 14
const PAGE_RIGHT = 196
const LIGHT_GRAY: [number, number, number] = [245, 245, 245]
const TEXT_DARK: [number, number, number] = [30, 30, 30]
const LINE_GRAY: [number, number, number] = [200, 200, 200]

const formatMoney = (amount: number, currencyCode: string): string =>
  Currency(currencyCode as DineroFactory.Currency)(amount).toFormat()

const formatAddress = (address: OrderAddress): string[] => {
  const lines: string[] = []
  const name = [address.givenName, address.familyName].filter(Boolean).join(" ")
  if (name) lines.push(name)
  if (address.streetAddress) lines.push(address.streetAddress)
  if (address.streetAddress2) lines.push(address.streetAddress2)
  const cityLine = [address.postalCode, address.city].filter(Boolean).join(" ")
  if (cityLine) lines.push(cityLine)
  if (address.stateOrProvince) lines.push(address.stateOrProvince)
  if (address.country) lines.push(address.country)
  if (address.email) lines.push(address.email)
  if (address.telephoneNumber) lines.push(address.telephoneNumber)
  return lines
}

const drawSectionLine = (doc: jsPDF, yPos: number, label?: string): number => {
  doc.setDrawColor(...LINE_GRAY)
  doc.setLineWidth(0.3)
  doc.line(PAGE_LEFT, yPos, PAGE_RIGHT, yPos)
  if (label) {
    yPos += 6
    doc.setFontSize(10)
    doc.setFont("helvetica", "bold")
    doc.setTextColor(...TEXT_DARK)
    doc.text(label, PAGE_LEFT, yPos)
    doc.setFont("helvetica", "normal")
    yPos += 6
  } else {
    yPos += 4
  }
  return yPos
}

const getLastTableY = (doc: jsPDF): number =>
  (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY

const lightTableStyles = {
  theme: "striped" as const,
  headStyles: {
    fillColor: LIGHT_GRAY,
    textColor: TEXT_DARK,
    fontStyle: "bold" as const
  },
  styles: { fontSize: 9, textColor: TEXT_DARK },
  alternateRowStyles: { fillColor: [252, 252, 252] as [number, number, number] }
}

export const downloadOrderReceipt = (order: Order): void => {
  const doc = new jsPDF()
  const currency = order.currencyCode
  let yPos = 20

  // ── Header ──────────────────────────────────────────────────────────
  doc.setFontSize(18)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(...TEXT_DARK)
  doc.text("Order Receipt", PAGE_LEFT, yPos)
  yPos += 4
  doc.setDrawColor(...LINE_GRAY)
  doc.setLineWidth(0.5)
  doc.line(PAGE_LEFT, yPos, PAGE_RIGHT, yPos)
  yPos += 8

  // Order meta — compact two-column layout
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.text(`Order #${order.reference}`, PAGE_LEFT, yPos)
  doc.text(dayjs(order.date).format("YYYY-MM-DD HH:mm"), PAGE_RIGHT, yPos, {
    align: "right"
  })
  yPos += 6
  doc.text(`Currency: ${currency}`, PAGE_LEFT, yPos)
  doc.text(`Country: ${order.countryCode}`, PAGE_RIGHT, yPos, { align: "right" })
  yPos += 6

  // Cancelled status
  if (order.status.orderStates.includes(OrderState.Cancelled)) {
    doc.setTextColor(255, 0, 0)
    doc.setFont("helvetica", "bold")
    doc.text("CANCELLED", PAGE_RIGHT, yPos, { align: "right" })
    doc.setTextColor(...TEXT_DARK)
    doc.setFont("helvetica", "normal")
    yPos += 6
  }
  yPos += 4

  // ── Customer ────────────────────────────────────────────────────────
  yPos = drawSectionLine(doc, yPos, "Customer")

  doc.setFontSize(9)
  doc.setFont("helvetica", "bold")
  doc.text("Billing Address", PAGE_LEFT, yPos)
  doc.text("Shipping Address", 110, yPos)
  doc.setFont("helvetica", "normal")
  yPos += 5

  const billingLines = formatAddress(order.billingAddress)
  const shippingLines = formatAddress(order.shippingAddress)
  const maxLines = Math.max(billingLines.length, shippingLines.length)

  for (let i = 0; i < maxLines; i++) {
    if (billingLines[i]) doc.text(billingLines[i], PAGE_LEFT, yPos)
    if (shippingLines[i]) doc.text(shippingLines[i], 110, yPos)
    yPos += 5
  }
  yPos += 6

  // ── Refund helpers ──────────────────────────────────────────────────
  const getRefundedAmount = (orderLineId: string): number => {
    const refundTotal = (order.Refunds ?? [])
      .filter((r) => r.completed)
      .flatMap((r) => r.orderLines)
      .filter((ol) => ol.orderLineId === orderLineId)
      .reduce((sum, ol) => sum + ol.totalAmount, 0)

    const compensationTotal = (order.Compensations ?? [])
      .filter((c) => c.completed)
      .flatMap((c) => c.orderLines ?? [])
      .filter((ol) => ol.orderLineId === orderLineId)
      .reduce((sum, ol) => sum + ol.totalDiscountAmount, 0)

    const releaseTotal = (order.Releases ?? [])
      .filter((r) => r.completed)
      .flatMap((r) => r.orderLines)
      .filter((ol) => ol.orderLineId === orderLineId)
      .reduce((sum, ol) => sum + ol.totalAmount, 0)

    return refundTotal + compensationTotal + releaseTotal
  }

  const hasRefunds = (order.orderLines ?? []).some(
    (line) => getRefundedAmount(line.id) > 0
  )

  // ── Order Items ─────────────────────────────────────────────────────
  yPos = drawSectionLine(doc, yPos, "Order Items")

  const orderLineHeaders = hasRefunds
    ? [["Item", "Qty", "Unit Price", "Discount", "Tax", "Total", "Refunded"]]
    : [["Item", "Qty", "Unit Price", "Discount", "Tax", "Total"]]

  const orderLineRows = (order.orderLines ?? []).map((line) => {
    const row = [
      line.displayName,
      String(line.quantity),
      formatMoney(line.basePriceAmount, currency),
      formatMoney(line.totalDiscountAmount, currency),
      formatMoney(line.totalTaxAmount, currency),
      formatMoney(line.totalPriceAmount, currency)
    ]
    if (hasRefunds) {
      const refunded = getRefundedAmount(line.id)
      row.push(refunded > 0 ? formatMoney(refunded, currency) : "-")
    }
    return row
  })

  autoTable(doc, {
    startY: yPos,
    head: orderLineHeaders,
    body: orderLineRows,
    ...lightTableStyles,
    columnStyles: {
      0: { cellWidth: hasRefunds ? 44 : 56 },
      1: { halign: "center", cellWidth: 12 },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
      ...(hasRefunds ? { 6: { halign: "right" as const } } : {})
    }
  })
  yPos = getLastTableY(doc) + 10

  // ── Gifts ───────────────────────────────────────────────────────────
  if (order.gifts && order.gifts.length > 0) {
    yPos = drawSectionLine(doc, yPos, "Gifts")

    const giftRows = order.gifts.map((gift) => [
      gift.displayName,
      String(gift.quantity),
      formatMoney(gift.totalPriceAmount, currency)
    ])

    autoTable(doc, {
      startY: yPos,
      head: [["Gift Item", "Qty", "Value"]],
      body: giftRows,
      ...lightTableStyles,
      columnStyles: {
        1: { halign: "center", cellWidth: 12 },
        2: { halign: "right" }
      }
    })
    yPos = getLastTableY(doc) + 10
  }

  // ── Gift Card Products ──────────────────────────────────────────────
  const giftCardProducts =
    order.GiftCardProduct?.providerName === "Retain24"
      ? (order.GiftCardProduct as OrderGiftCardProductRetain24).giftCardProducts ?? []
      : []
  if (giftCardProducts.length > 0) {
    yPos = drawSectionLine(doc, yPos, "Gift Card Products")

    const giftCardProductRows = giftCardProducts.map((product) => [
      product.name,
      formatMoney(product.amount, currency)
    ])

    autoTable(doc, {
      startY: yPos,
      head: [["Gift Card Product", "Value"]],
      body: giftCardProductRows,
      ...lightTableStyles,
      columnStyles: {
        1: { halign: "right" }
      }
    })
    yPos = getLastTableY(doc) + 10
  }

  // ── Shipping ────────────────────────────────────────────────────────
  if (order.shippingFees && order.shippingFees.length > 0) {
    yPos = drawSectionLine(doc, yPos, "Shipping")

    const shippingRows = order.shippingFees.map((fee) => [
      fee.name ?? fee.displayName ?? "Shipping",
      formatMoney(fee.taxAmount ?? 0, currency),
      formatMoney(fee.basePriceAmount ?? 0, currency)
    ])

    autoTable(doc, {
      startY: yPos,
      head: [["Shipping", "Tax", "Amount"]],
      body: shippingRows,
      ...lightTableStyles,
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" }
      }
    })
    yPos = getLastTableY(doc) + 10
  }

  // ── Payment Summary ─────────────────────────────────────────────────
  yPos = drawSectionLine(doc, yPos, "Payment Summary")

  const totals = order.totals
  const summaryLines: { label: string; value: string }[] = [
    { label: "Subtotal", value: formatMoney(totals.subTotal, currency) },
    { label: "Tax", value: formatMoney(totals.taxTotal, currency) },
    { label: "Shipping", value: formatMoney(totals.shippingTotal, currency) }
  ]
  if (totals.discountTotal > 0) {
    summaryLines.push({
      label: "Discount",
      value: `-${formatMoney(totals.discountTotal, currency)}`
    })
  }

  const isCancelled = order.status.orderStates.includes(OrderState.Cancelled)
  const totalRefunded = isCancelled
    ? totals.grandTotal
    : (order.orderLines ?? []).reduce(
        (sum, line) => sum + getRefundedAmount(line.id),
        0
      )

  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  const labelX = 120
  const valueX = PAGE_RIGHT

  for (const line of summaryLines) {
    doc.text(line.label, labelX, yPos)
    doc.text(line.value, valueX, yPos, { align: "right" })
    yPos += 6
  }

  // Separator line before Grand Total
  yPos += 1
  doc.setDrawColor(...LINE_GRAY)
  doc.setLineWidth(0.3)
  doc.line(labelX, yPos, PAGE_RIGHT, yPos)
  yPos += 6

  // Grand Total — raw value, bold
  doc.setFontSize(11)
  doc.setFont("helvetica", "bold")
  doc.text("Grand Total", labelX, yPos)
  doc.text(formatMoney(totals.grandTotal, currency), valueX, yPos, { align: "right" })
  doc.setFont("helvetica", "normal")
  yPos += 8

  // Deduction lines and Customer Paid — only if deductions exist
  const hasDeductions =
    totals.giftCardTotal > 0 || totals.bonusTotal > 0 || totalRefunded > 0
  if (hasDeductions) {
    doc.setFontSize(9)
    if (totals.giftCardTotal > 0) {
      doc.text("Gift Card", labelX, yPos)
      doc.text(`-${formatMoney(totals.giftCardTotal, currency)}`, valueX, yPos, {
        align: "right"
      })
      yPos += 5
    }
    if (totals.bonusTotal > 0) {
      doc.text("Bonus", labelX, yPos)
      doc.text(`-${formatMoney(totals.bonusTotal, currency)}`, valueX, yPos, {
        align: "right"
      })
      yPos += 5
    }
    if (totalRefunded > 0) {
      doc.text("Refunded", labelX, yPos)
      doc.text(`-${formatMoney(totalRefunded, currency)}`, valueX, yPos, {
        align: "right"
      })
      yPos += 5
    }

    // Separator line before Customer Paid
    yPos += 1
    doc.setDrawColor(...LINE_GRAY)
    doc.setLineWidth(0.3)
    doc.line(labelX, yPos, PAGE_RIGHT, yPos)
    yPos += 5

    const customerPaid = Math.max(
      0,
      totals.grandTotal - totals.giftCardTotal - totals.bonusTotal - totalRefunded
    )
    doc.setFontSize(10)
    doc.setFont("helvetica", "bold")
    doc.text("Customer Paid", labelX, yPos)
    doc.text(formatMoney(customerPaid, currency), valueX, yPos, { align: "right" })
    doc.setFont("helvetica", "normal")
    yPos += 10
  } else {
    yPos += 2
  }

  // ── Footer ──────────────────────────────────────────────────────────
  doc.setDrawColor(...LINE_GRAY)
  doc.setLineWidth(0.2)
  doc.line(PAGE_LEFT, yPos, PAGE_RIGHT, yPos)
  yPos += 5
  doc.setFontSize(8)
  doc.setTextColor(120, 120, 120)
  doc.text(
    order.isTaxIncludedInPrice ? "Prices include tax." : "Prices exclude tax.",
    PAGE_LEFT,
    yPos
  )
  if (order.isTaxExempt) {
    doc.text("This order is tax exempt.", PAGE_LEFT, yPos + 4)
  }

  doc.save(`receipt-${order.reference}.pdf`)
}
